import { defineComponent, ref, computed, reactive } from 'vue'
import CompanyMenuForm from '@/modules/company/components/CompanyMenuForm/CompanyMenuForm.vue'
import { useCompanyStore } from '@/store/company'
import { useAuthStore } from '@/store/auth/auth'
import { useRoute, useRouter, RouterLink } from 'vue-router'
import type { DefaultError } from '@/types/httpError'

interface MenuItemChildren {
  title: string
  path: string
}
type MenuItems = Record<
  string,
  { icon: string, title: string, path: string, children?: MenuItemChildren[] }[]
>

export default defineComponent({
  name: 'MenuComponent',
  components: {
    CompanyMenuForm,
    RouterLink,
  },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const companyStore = useCompanyStore()
    const authStore = useAuthStore()

    const companySectionsMenu = computed(() => companyStore.menuSections ?? [])
    const isConfirmPopupOpen = ref(false)
    const isCompanyMenuItemFormOpen = ref(false)
    const selectedCompanyMenuItemId = ref<number | string>('')

    const isReqLoading = ref(false)
    const reqError = ref('')

    const menuItems = reactive<MenuItems>({
      top: [
        {
          icon: 'puzzle',
          title: 'Оформление и публикация',
          path: '/builder',
        },
        {
          icon: 'home',
          title: 'Главная страница',
          path: '/',
        },
        {
          icon: 'services',
          title: 'Услуги',
          path: '/services',
          children: [
            {
              title: 'Категории',
              path: '/categories',
            },
            {
              title: 'Список услуг',
              path: '/search',
            },
          ],
        },
        {
          icon: 'specialists',
          title: 'Специалисты',
          path: '/specialists',
        },
        {
          icon: 'appointment',
          title: 'Записи',
          path: '/appointments',
          children: [
            {
              title: 'Список',
              path: '/list',
            },
            {
              title: 'Календарь',
              path: '/calendar',
            },
          ],
        },
        {
          icon: 'catalog',
          title: 'Каталог',
          path: '/catalog',
          children: [
            {
              title: 'Категории',
              path: '/categories',
            },
            {
              title: 'Товары',
              path: '/products',
            },
            // {
            //   title: 'Комплекты',
            //   path: '/kits',
            // },
          ],
        },
        {
          icon: 'shops',
          title: 'Филиалы',
          path: '/shops',
        },
        {
          icon: 'orders',
          title: 'Заказы',
          path: '/orders',
        },
        {
          icon: 'clients',
          title: 'Клиенты',
          path: '/clients',
        },
        {
          icon: 'info',
          title: 'О компании',
          path: '/company',
          children: [],
        },
        {
          icon: 'news',
          title: 'Новости',
          path: '/news',
        },
        {
          icon: 'announcement',
          title: 'Push-рассылка',
          path: '/push',
        },
      ],
      bottom: [
        {
          icon: 'money2',
          title: 'Финансы',
          path: '/finance',
        },
        {
          icon: 'setting',
          title: 'Настройки',
          path: '/settings',
        },
        {
          icon: 'api',
          title: 'API',
          path: '/api/webhook/create',
        },
        {
          icon: 'intersect-circle',
          title: 'Интеграции',
          path: '/integrations',
        },
      ],
    })

    const adminMenuItems: MenuItems = {
      top: [
        {
          icon: 'clients',
          title: 'Компании',
          path: '/admin/companies',
        },
        {
          icon: 'partners',
          title: 'Партнеры',
          path: '/admin/partners',
        },
        {
          icon: 'money2',
          title: 'Финансы',
          path: '/admin/finance',
        },
      ],
      bottom: [
        {
          icon: 'setting',
          title: 'Настройки',
          path: '/admin/settings',
        },
      ],
    }

    const menu = computed(() => (authStore.isAdmin ? adminMenuItems : menuItems))

    const isSelect = (parent: string) => {
      return route.fullPath.includes(parent)
    }

    const openedItem = ref<string[]>(['/catalog', '/services', '/appointments'])
    const isOpen = (path: string) => openedItem.value.includes(path)
    const openChildren = (path: string) => {
      if (isOpen(path)) {
        openedItem.value = openedItem.value.filter(item => item !== path)
      } else {
        openedItem.value.push(path)
      }
    }

    const onDeleteCompanyMenuItem = (id: number) => {
      selectedCompanyMenuItemId.value = id
      isConfirmPopupOpen.value = true
    }

    const handleCompanyMenuItemDelete = async () => {
      isReqLoading.value = true
      const id = selectedCompanyMenuItemId.value
      try {
        await companyStore.deleteMenuSection({ id: +id })
        onConfirmPopupClose()
        if (isSelect(`/company/${id}`)) {
          await router.push('/company')
        }
      } catch (error) {
        const typedError = error as DefaultError
        reqError.value = typedError.error
      } finally {
        isReqLoading.value = false
      }
    }

    const onConfirmPopupClose = () => {
      isConfirmPopupOpen.value = false
      selectedCompanyMenuItemId.value = ''
    }

    const toggleIsCompanyMenuItemFormOpen = () => {
      isCompanyMenuItemFormOpen.value = !isCompanyMenuItemFormOpen.value
    }

    return {
      menu,
      menuItems,
      companySectionsMenu,
      isConfirmPopupOpen,
      isCompanyMenuItemFormOpen,
      isReqLoading,
      reqError,
      openChildren,
      isOpen,
      isSelect,
      onDeleteCompanyMenuItem,
      handleCompanyMenuItemDelete,
      onConfirmPopupClose,
      toggleIsCompanyMenuItemFormOpen,
      authStore,
    }
  },
})
